import React, { useState } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { ButtonGroup, Text, InternalGrid } from '@paygreen/paygreen-ui';
import { useStripe } from '@stripe/react-stripe-js';
import {
    SimpleActionButton,
    ValidateButton,
    ModalForm,
    EditButton,
    Message,
    Loader,
} from '../../../../components';
import { patchRegistration } from '../../service';

const CharityManagementModal = ({
    t,
    setOpen,
    isOpen,
    accountData,
    setAccountData,
}) => {
    const stripe = useStripe();

    const associationName = accountData?.association?.name;
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [isLoading, setIsLoading] = useState();

    /**
     * POST request to stripe to generate account with token for charity
     * @returns {object} - object with token account
     */
    const createStripeAccount = () => {
        if (!stripe) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        stripe
            .createToken('account', {
                business_type: 'company',
                company: {
                    name: accountData?.association?.name,
                    address: {
                        line1: accountData?.postalAddress?.addressLine1,
                        line2: accountData?.postalAddress?.addressLine2 || '',
                        city: accountData?.postalAddress?.city,
                        state: accountData?.postalAddress?.region || '',
                        postal_code: accountData?.postalAddress?.zipCode,
                        country: accountData?.postalAddress?.country,
                    },
                },
                tos_shown_and_accepted: true,
            })
            .then((data) => {
                if (data.error) {
                    setErrorMessage(data.error.message);
                    setIsLoading(false);
                } else {
                    sendToken(
                        accountData?.association?.idAssociation,
                        data.token.id,
                    );
                }
            });
    };

    /**
     * PATCH request to API to transmit token received from stripe
     */
    const sendToken = (idAssociation, token) => {
        patchRegistration(idAssociation, token)
            .then(() => {
                setIsSuccess(true);
            })
            .catch((error) => {
                const { detail, status } = error.response.data;

                setErrorMessage(status + ' / ' + detail);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const cancelCloseModal = () => {
        if (isSuccess) {
            setAccountData({
                ...accountData,
                onboardingCompleted: true,
            });
        }

        setErrorMessage();
        setOpen(false);
    };

    return (
        <ModalForm
            modalTitle={t('admin.charity.stripeAccount.noneTitle')}
            blockWidth="md"
            paddingLateral="md"
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            {!isSuccess ? (
                <Text textSize="sm">
                    <Trans i18nKey="admin.charity.stripeAccount.noneMessage">
                        before creating
                        <Text textSize="sm" htmlTag="strong">
                            {{ associationName: associationName || '' }}
                        </Text>
                        verify
                    </Trans>
                </Text>
            ) : (
                <Text textSize="sm">
                    <Trans i18nKey="admin.charity.stripeAccount.successMessage">
                        account created
                        <Text textSize="sm" htmlTag="strong">
                            {{ associationName: associationName || '' }}
                        </Text>
                        fill infos
                    </Trans>
                </Text>
            )}

            {errorMessage && (
                <InternalGrid justifyContent="center">
                    <Message
                        message={errorMessage}
                        type="error"
                        marginTop="md"
                        marginBottom="md"
                        paddingLateral="lg"
                    />
                </InternalGrid>
            )}

            <ButtonGroup marginTop="sm" marginBottom="xs">
                {!isSuccess ? (
                    <>
                        <SimpleActionButton
                            onClick={cancelCloseModal}
                            buttonSize="md"
                            icon="ArrowRoundTopLeftIcon"
                        >
                            {t('admin.charity.verifyInfos')}
                        </SimpleActionButton>

                        <ValidateButton
                            buttonSize="md"
                            onClick={createStripeAccount}
                            icon="PlusIcon"
                        >
                            {t('admin.charity.createAccount')}
                        </ValidateButton>
                    </>
                ) : (
                    <a
                        href="https://dashboard.stripe.com/connect/accounts/overview"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <EditButton icon="OutIcon">
                            {t('admin.charity.openStripe')}
                        </EditButton>
                    </a>
                )}
            </ButtonGroup>

            {isLoading && <Loader isLoading={isLoading} />}
        </ModalForm>
    );
};

CharityManagementModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    accountData: PropTypes.object,
    setIsLoading: PropTypes.func,
    setAccountData: PropTypes.func,
};

CharityManagementModal.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(CharityManagementModal);
